import React, { useState } from 'react';
import { Table, Tag, Space, Select, Input, Card, Row, Col, Statistic, Button } from 'antd';
import { MailOutlined, ReloadOutlined } from '@ant-design/icons';
import { PageHeader } from 'components/headings';
import useEnviosCorreoList, { FILTROS_POR_DEFECTO } from 'hooks/useEnviosCorreoList';
import EnvioCorreoDetailDrawer from './EnvioCorreoDetailDrawer';
import { ETIQUETAS_ESTADO, ETIQUETAS_TIPO, colorEstado } from './etiquetas';

// Valor centinela del selector para "sin filtro": el Select de antd es
// controlado y con `value={undefined}` no marca ninguna opción. El centinela
// nunca viaja al backend: se convierte a `undefined` al armar el filtro.
const FILTRO_ESTADO_TODOS = 'todos';

export default function CorreosTabla({ titulo, consultas, columnasExtra = [] }) {
  const {
    rows,
    total,
    resumen,
    loading,
    page,
    setPage,
    pageSize,
    search,
    setSearch,
    filters,
    setFilters,
    refresh,
  } = useEnviosCorreoList(FILTROS_POR_DEFECTO, consultas);
  const [seleccionado, setSeleccionado] = useState(null);

  const columnas = [
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      render: (v) => (v ? new Date(v).toLocaleString('es-CL') : '—'),
      width: 170,
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      render: (v) => ETIQUETAS_TIPO[v] || v,
    },
    ...columnasExtra,
    {
      title: 'Destinatario',
      dataIndex: 'destinatarios',
      render: (d) => (d?.to || []).join(', ') || '—',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      width: 120,
      render: (v) => <Tag color={colorEstado(v)}>{ETIQUETAS_ESTADO[v] || v}</Tag>,
    },
    {
      title: '',
      key: 'acciones',
      width: 100,
      render: (_, fila) => (
        <Button type="link" onClick={() => setSeleccionado(fila.id)}>
          Ver
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={titulo}
        subtitle="Comprobantes, informes y avisos enviados por el sistema"
        icon={MailOutlined}
        actions={
          <Button icon={<ReloadOutlined />} onClick={refresh}>
            Actualizar
          </Button>
        }
      />

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={8}>
          <Card>
            <Statistic title="No llegaron (7 días)" value={resumen.fallidos} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="No se enviaron (7 días)" value={resumen.omitidos} />
          </Card>
        </Col>
        <Col span={8}>
          <Card>
            <Statistic title="Enviados (7 días)" value={resumen.enviados} />
          </Card>
        </Col>
      </Row>

      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          style={{ width: 220 }}
          value={filters.estado ?? FILTRO_ESTADO_TODOS}
          onChange={(estado) => {
            setPage(1);
            setFilters({
              ...filters,
              estado: estado === FILTRO_ESTADO_TODOS ? undefined : estado,
            });
          }}
          options={[
            { value: 'fallido,omitido', label: 'Requieren atención' },
            { value: 'fallido', label: 'No llegaron' },
            { value: 'omitido', label: 'No se enviaron' },
            { value: 'enviado', label: 'Enviados' },
            { value: FILTRO_ESTADO_TODOS, label: 'Todos' },
          ]}
        />
        <Input.Search
          allowClear
          placeholder="Buscar por destinatario o asunto"
          style={{ width: 280 }}
          onSearch={(v) => {
            setPage(1);
            setSearch(v);
          }}
        />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        columns={columnas}
        dataSource={rows}
        pagination={{
          current: page,
          pageSize,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
      />

      <EnvioCorreoDetailDrawer
        envioId={seleccionado}
        open={seleccionado != null}
        onClose={() => setSeleccionado(null)}
        onReintentado={refresh}
      />
    </div>
  );
}
